import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import {
  CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, formatApiError } from "@/lib/api";

const RISK_COLORS = { "LÅG": "#10b981", "MEDEL": "#f59e0b", "HÖG": "#ef4444" };

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => {
    api.get("/analytics/trends").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/reports/annual-evaluation/pdf/${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Reko_Arsrapport_${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Årsrapporten har exporterats som PDF");
    } catch (e) {
      toast.error(formatApiError(e, "PDF-exporten misslyckades"));
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <div className="text-sm text-slate-500">Laddar statistik...</div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="analytics-view">
      <div className="flex justify-between items-center border-b border-slate-200 pb-4 mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Statistik, Trender & PDF-Export</h2>
          <p className="text-sm text-slate-500 mt-1">Aggregerad kvalitetsdata och formella rapporter för FAR-kontroll.</p>
        </div>
        <button
          data-testid="pdf-export-btn" onClick={exportPdf} disabled={exporting}
          className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-xs font-bold px-4 py-2 rounded shadow flex items-center"
        >
          <FileText className="w-3.5 h-3.5 mr-1.5" />
          {exporting ? "Exporterar..." : `Exportera Årsrapport ${year} (PDF)`}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-xs">
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="text-slate-500 font-medium uppercase tracking-wider">Kunder totalt</div>
          <div className="text-2xl font-extrabold text-slate-900 mt-1" data-testid="analytics-total-clients">{data.total_clients} st</div>
        </div>
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="text-slate-500 font-medium uppercase tracking-wider">Policy-signering</div>
          <div className="text-2xl font-extrabold text-slate-900 mt-1" data-testid="analytics-signing-rate">{data.policy_signature_rate}%</div>
        </div>
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="text-slate-500 font-medium uppercase tracking-wider">Automatiska jobb</div>
          <div className="text-2xl font-extrabold text-emerald-600 mt-1">Aktiva</div>
          <p className="text-slate-500 mt-1">Riskanalys-timer, deadline- & policy-påminnelser via e-post</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="text-xs font-bold text-slate-700 mb-3">Avvikelseutveckling över tid</h3>
          <div data-testid="trend-chart">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.months}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="enstaka" name="Enstaka händelser" stroke="#f59e0b" strokeWidth={2} />
                <Line type="monotone" dataKey="generella" name="Generella brister" stroke="#ef4444" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="text-xs font-bold text-slate-700 mb-3">Kunders Riskfördelning</h3>
          <div data-testid="risk-pie-chart">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.risk_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => `${name}: ${value}`} labelLine={false} fontSize={11}>
                  {data.risk_distribution.map((entry) => (
                    <Cell key={entry.name} fill={RISK_COLORS[entry.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
